import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { accountMappingService } from "~/services/contabilidad/accountMappingService";
import type {
  MappingTemplateFilters,
  MappingTemplateFormData,
} from "~/types/contabilidad";

export const MAPPING_TEMPLATES_QUERY_KEY = ["mappingTemplates"];

export function useMappingTemplates(filters?: MappingTemplateFilters) {
  return useQuery({
    queryKey: [...MAPPING_TEMPLATES_QUERY_KEY, filters],
    queryFn: () => accountMappingService.getTemplates(filters),
  });
}

export function useMappingTemplate(id: string) {
  return useQuery({
    queryKey: [...MAPPING_TEMPLATES_QUERY_KEY, id],
    queryFn: () => accountMappingService.getTemplateById(id),
    enabled: !!id,
  });
}

export function useCreateMappingTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MappingTemplateFormData) =>
      accountMappingService.createTemplate(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: MAPPING_TEMPLATES_QUERY_KEY });
      toast.success("Plantilla de mapeo creada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear plantilla: ${error.message}`);
    },
  });
}

export function useUpdateMappingTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<MappingTemplateFormData>;
    }) => accountMappingService.updateTemplate(id, data),
    onSuccess: (updatedTemplate, { id }) => {
      queryClient.invalidateQueries({ queryKey: MAPPING_TEMPLATES_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...MAPPING_TEMPLATES_QUERY_KEY, id],
      });
      toast.success("Plantilla de mapeo actualizada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar plantilla: ${error.message}`);
    },
  });
}

export function useDeleteMappingTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountMappingService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAPPING_TEMPLATES_QUERY_KEY });
      toast.success("Plantilla de mapeo eliminada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar plantilla: ${error.message}`);
    },
  });
}

export function useToggleMappingTemplateActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      accountMappingService.toggleTemplateActive(id, isActive),
    onSuccess: (updatedTemplate, { id }) => {
      queryClient.invalidateQueries({ queryKey: MAPPING_TEMPLATES_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...MAPPING_TEMPLATES_QUERY_KEY, id],
      });
      toast.success(
        updatedTemplate.is_active ? "Plantilla activada" : "Plantilla desactivada"
      );
    },
    onError: (error: Error) => {
      toast.error(`Error al cambiar estado de plantilla: ${error.message}`);
    },
  });
}

export function useCopyMappingTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sourceTemplateId,
      newName,
      companyId,
    }: {
      sourceTemplateId: string;
      newName: string;
      companyId: string;
    }) => accountMappingService.copyTemplate(sourceTemplateId, newName, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAPPING_TEMPLATES_QUERY_KEY });
      toast.success("Plantilla copiada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al copiar plantilla: ${error.message}`);
    },
  });
}
